/**
 * Sleeper client (§4.1, FR-1, FR-2, FR-5).
 *
 * **Browser-direct.** `api.sleeper.app` serves `access-control-allow-origin: *`
 * (re-verified 2026-07-27), which is the single most important fact in the
 * architecture: the highest-priority feature — live draft state and the
 * drafted-vs-rankings board — runs entirely client-side, with zero server
 * involvement, zero cost, and no league ID, username, roster, or draft state
 * ever reaching this project's infrastructure (D-3, FR-63).
 *
 * Nothing in this file may be routed through a Pages Function. If a future
 * change needs one, it needs to justify itself against FR-63 first.
 */

import type {
  DraftPick,
  DraftState,
  Player,
  PlayerId,
  PlayerUniverse,
  Position,
} from '../core/types.ts';
import { FANTASY_POSITIONS } from '../core/types.ts';
import { normalizeName, normalizeTeam } from '../core/matching.ts';
import { fetchWithRetry, parseJsonByContent, sleeperLimiter } from './http.ts';

export const SLEEPER_BASE = 'https://api.sleeper.app/v1';

/**
 * Two request profiles, because "resilient" means opposite things in the two
 * places this client is used.
 *
 * **Background polling** should absorb a flaky connection without the user
 * noticing: long timeout, several retries, last-good state stays on screen
 * (FR-11). **Interactive setup calls** must fail fast — a visitor staring at
 * "Looking up your leagues…" has no idea anything is retrying, and a 12s
 * timeout with three attempts burns 39 seconds of a 60-second time-to-value
 * budget (NFR-8) before admitting the network is down.
 */
const INTERACTIVE = { timeoutMs: 6_000, retries: 1 } as const;
const BACKGROUND = { timeoutMs: 12_000, retries: 2 } as const;

async function get<T>(
  path: string,
  signal?: AbortSignal,
  profile: { timeoutMs: number; retries: number } = BACKGROUND,
): Promise<T> {
  await sleeperLimiter.acquire();
  const res = await fetchWithRetry(`${SLEEPER_BASE}${path}`, {
    signal,
    timeoutMs: profile.timeoutMs,
    retries: profile.retries,
  });
  return parseJsonByContent<T>(res);
}

// ---------------------------------------------------------------------------
// Raw payload shapes (only the fields actually used)
// ---------------------------------------------------------------------------

export interface SleeperUser {
  user_id: string;
  username: string;
  display_name: string;
  avatar: string | null;
}

export interface SleeperLeague {
  league_id: string;
  name: string;
  season: string;
  status: string;
  sport: string;
  total_rosters: number;
  roster_positions: string[];
  scoring_settings: Record<string, number>;
  settings: Record<string, number>;
  draft_id: string | null;
  avatar: string | null;
}

export interface SleeperDraft {
  draft_id: string;
  league_id: string;
  status: string;
  type: string;
  season: string;
  settings: Record<string, number>;
  draft_order: Record<string, number> | null;
  slot_to_roster_id: Record<string, number> | null;
  start_time: number | null;
  metadata?: Record<string, unknown>;
}

export interface SleeperPick {
  pick_no: number;
  round: number;
  draft_slot: number;
  roster_id: number | null;
  player_id: string;
  picked_by: string;
  is_keeper?: boolean | null;
  metadata?: Record<string, string>;
}

export interface SleeperRosterEntry {
  roster_id: number;
  owner_id: string | null;
  players: string[] | null;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export const getUser = (username: string, signal?: AbortSignal): Promise<SleeperUser> =>
  get<SleeperUser>(`/user/${encodeURIComponent(username.trim())}`, signal, INTERACTIVE);

export const getLeagues = (
  userId: string,
  season: string,
  signal?: AbortSignal,
): Promise<SleeperLeague[]> =>
  get<SleeperLeague[]>(
    `/user/${encodeURIComponent(userId)}/leagues/nfl/${encodeURIComponent(season)}`,
    signal,
    INTERACTIVE,
  );

export const getLeague = (leagueId: string, signal?: AbortSignal): Promise<SleeperLeague> =>
  get<SleeperLeague>(`/league/${encodeURIComponent(leagueId)}`, signal);

export const getLeagueUsers = (
  leagueId: string,
  signal?: AbortSignal,
): Promise<(SleeperUser & { metadata?: Record<string, string> })[]> =>
  get(`/league/${encodeURIComponent(leagueId)}/users`, signal);

export const getRosters = (
  leagueId: string,
  signal?: AbortSignal,
): Promise<SleeperRosterEntry[]> =>
  get<SleeperRosterEntry[]>(`/league/${encodeURIComponent(leagueId)}/rosters`, signal);

export const getDrafts = (leagueId: string, signal?: AbortSignal): Promise<SleeperDraft[]> =>
  get<SleeperDraft[]>(`/league/${encodeURIComponent(leagueId)}/drafts`, signal, INTERACTIVE);

export const getDraft = (draftId: string, signal?: AbortSignal): Promise<SleeperDraft> =>
  get<SleeperDraft>(`/draft/${encodeURIComponent(draftId)}`, signal);

export const getPicks = (draftId: string, signal?: AbortSignal): Promise<SleeperPick[]> =>
  get<SleeperPick[]>(`/draft/${encodeURIComponent(draftId)}/picks`, signal);

export const getNflState = (signal?: AbortSignal): Promise<{ season: string; week: number; season_type: string }> =>
  get(`/state/nfl`, signal);

// ---------------------------------------------------------------------------
// Player universe (§4.1 caching requirement)
// ---------------------------------------------------------------------------

/**
 * Fetch the full NFL player map.
 *
 * This payload is ~14MB uncompressed and is explicitly not meant to be called
 * per lookup — Sleeper asks that it be stored and refreshed at most once a day.
 * Callers must go through the IndexedDB-backed cache in `state/playerCache.ts`
 * rather than calling this directly; it is exported separately so the cache
 * layer can own the TTL and this module can stay a thin client.
 */
export async function fetchPlayerUniverse(signal?: AbortSignal): Promise<PlayerUniverse> {
  await sleeperLimiter.acquire();
  const res = await fetchWithRetry(`${SLEEPER_BASE}/players/nfl`, {
    signal,
    timeoutMs: 90_000,
    retries: 1,
  });
  const raw = await parseJsonByContent<Record<string, RawPlayer>>(res);
  return normalizePlayerUniverse(raw);
}

interface RawPlayer {
  player_id?: string;
  full_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  position?: string | null;
  fantasy_positions?: string[] | null;
  team?: string | null;
  age?: number | null;
  years_exp?: number | null;
  active?: boolean | null;
  injury_status?: string | null;
  injury_body_part?: string | null;
  injury_notes?: string | null;
  injury_start_date?: string | null;
  practice_participation?: string | null;
  practice_description?: string | null;
  news_updated?: number | null;
  status?: string | null;
  search_rank?: number | null;
}

/** Sleeper's sentinel for "not ranked at all". */
const UNRANKED = 9_999_999;

export function normalizePlayerUniverse(raw: Record<string, RawPlayer>): PlayerUniverse {
  const universe: PlayerUniverse = new Map();

  for (const [id, p] of Object.entries(raw)) {
    const position = asPosition(p.position);
    const fantasyPositions = (p.fantasy_positions ?? [])
      .map(asPosition)
      .filter((x): x is Position => x !== null);

    // Only fantasy-relevant players. The raw payload carries every offensive
    // lineman and long snapper in the league; keeping them would triple the
    // fuzzy-match search space for no benefit.
    if (!position && fantasyPositions.length === 0) continue;
    if (position && !FANTASY_POSITIONS.includes(position) && fantasyPositions.length === 0) continue;

    // Team defenses have no full_name; Sleeper splits them into city + nickname.
    const full_name =
      p.full_name?.trim() ||
      [p.first_name, p.last_name].filter(Boolean).join(' ').trim() ||
      id;

    universe.set(id, {
      player_id_sleeper: id,
      full_name,
      normalized_name: normalizeName(full_name),
      position: position ?? fantasyPositions[0] ?? null,
      fantasy_positions: fantasyPositions.length ? fantasyPositions : position ? [position] : [],
      team: normalizeTeam(p.team) ?? (position === 'DEF' ? normalizeTeam(id) : null),
      // Sleeper's player payload carries no bye week (verified 2026-07-27);
      // it is backfilled from FFC ADP, which does. See NOTES.md.
      bye_week: null,
      age: p.age ?? null,
      years_exp: p.years_exp ?? null,
      injury_status: p.injury_status ?? null,
      active: p.active ?? null,
      injury_body_part: p.injury_body_part ?? null,
      injury_notes: p.injury_notes ?? null,
      injury_start_date: p.injury_start_date ?? null,
      practice_participation: p.practice_participation ?? null,
      practice_description: p.practice_description ?? null,
      news_updated: p.news_updated ?? null,
      status: p.status ?? (p.active ? 'Active' : null),
      search_rank: p.search_rank == null || p.search_rank >= UNRANKED ? null : p.search_rank,
    });
  }

  return universe;
}

function asPosition(value: string | null | undefined): Position | null {
  if (!value) return null;
  const v = value.toUpperCase();
  if (v === 'DST') return 'DEF';
  if (v === 'FB') return 'RB';
  return (FANTASY_POSITIONS as readonly string[]).includes(v) ? (v as Position) : null;
}

// ---------------------------------------------------------------------------
// Draft state assembly (FR-5, FR-6)
// ---------------------------------------------------------------------------

export interface DraftContext {
  draft: SleeperDraft;
  /** Sleeper user_id → display name, for pick attribution. */
  displayNames: Map<string, string>;
  /** roster_id → display name, for drafts keyed by roster. */
  rosterNames: Map<number, string>;
  /**
   * Each team's roster as Sleeper reports it right now, independent of this
   * draft event. Empty for a fresh startup draft, but a dynasty rookie draft
   * begins with every keeper already assigned — `toDraftState` needs this to
   * know those players are unavailable even though they were never picked in
   * *this* draft.
   */
  rosters: SleeperRosterEntry[];
  /** The signed-in user's draft slot, if they are in this draft. */
  myDraftSlot: number | null;
  myUserId: string | null;
}

export function buildDraftContext(
  draft: SleeperDraft,
  users: (SleeperUser & { metadata?: Record<string, string> })[],
  rosters: SleeperRosterEntry[],
  myUserId: string | null,
): DraftContext {
  const displayNames = new Map<string, string>();
  for (const user of users) {
    displayNames.set(user.user_id, user.metadata?.['team_name'] || user.display_name || user.username);
  }

  const rosterNames = new Map<number, string>();
  for (const roster of rosters) {
    const name = roster.owner_id ? displayNames.get(roster.owner_id) : undefined;
    rosterNames.set(roster.roster_id, name ?? `Team ${roster.roster_id}`);
  }

  let myDraftSlot: number | null = null;
  if (myUserId && draft.draft_order) {
    myDraftSlot = draft.draft_order[myUserId] ?? null;
  }

  return { draft, displayNames, rosterNames, rosters, myDraftSlot, myUserId };
}

/**
 * Fold raw picks into the engine's `DraftState`.
 *
 * `manualPicks` are merged in so FR-12 (hand-entered picks) works identically
 * to synced ones everywhere downstream — the board, the engine, and the roster
 * panel cannot tell the difference, which is the point: when Sleeper sync
 * breaks mid-draft the tool has to keep working, not half-work.
 */
export function toDraftState(
  ctx: DraftContext,
  picks: SleeperPick[],
  manualPicks: DraftPick[] = [],
  teams = 12,
): DraftState {
  const synced: DraftPick[] = picks.map((p) => ({
    pick_no: p.pick_no,
    round: p.round,
    draft_slot: p.draft_slot,
    roster_id: p.roster_id,
    player_id: p.player_id,
    picked_by: p.picked_by || null,
    team_name:
      (p.picked_by ? ctx.displayNames.get(p.picked_by) : undefined) ??
      (p.roster_id !== null ? ctx.rosterNames.get(p.roster_id) : undefined) ??
      `Slot ${p.draft_slot}`,
  }));

  // A manual entry for a player Sleeper has since reported is redundant; the
  // synced record wins because it carries the real pick number and team.
  const syncedPlayers = new Set(synced.map((p) => p.player_id));
  const merged = [
    ...synced,
    ...manualPicks.filter((p) => !syncedPlayers.has(p.player_id)),
  ].sort((a, b) => a.pick_no - b.pick_no);

  const current_pick_no = merged.length + 1;
  const rounds = ctx.draft.settings?.['rounds'] ?? 15;
  const type = ctx.draft.type ?? 'snake';

  const all_rosters: Record<number, PlayerId[]> = {};

  // Seed with each team's roster as Sleeper reports it right now. A fresh
  // startup draft has nothing here yet, so this is a no-op — but a dynasty
  // rookie draft starts with every keeper already assigned to a team, and
  // those players must count as unavailable even though they will never
  // appear in `picks` (they were never picked in *this* draft event).
  for (const roster of ctx.rosters) {
    if (roster.players?.length) all_rosters[roster.roster_id] = [...roster.players];
  }

  for (const pick of merged) {
    const slot = pick.draft_slot;
    const key = ctx.draft.slot_to_roster_id?.[String(slot)] ?? slot;
    const bucket = (all_rosters[key] ??= []);
    // Sleeper's roster snapshot updates live during an in-progress draft, so a
    // pick already reflected there must not be double-counted here.
    if (!bucket.includes(pick.player_id)) bucket.push(pick.player_id);
  }

  const mySlot = ctx.myDraftSlot;
  const myRosterKey =
    mySlot !== null ? ctx.draft.slot_to_roster_id?.[String(mySlot)] ?? mySlot : null;

  const on_the_clock_slot = slotForPick(current_pick_no, teams, type);
  const myPicks = mySlot === null ? [] : allPicksForSlot(mySlot, teams, rounds, type);
  const upcoming = myPicks.filter((p) => p >= current_pick_no);

  return {
    draft_id: ctx.draft.draft_id,
    status: ctx.draft.status,
    picks: merged,
    current_pick_no,
    on_the_clock_slot,
    my_draft_slot: mySlot,
    my_next_pick_no: upcoming[0] ?? null,
    my_pick_after_that: upcoming[1] ?? null,
    my_roster: myRosterKey !== null ? all_rosters[myRosterKey] ?? [] : [],
    all_rosters,
    last_synced_at: Date.now(),
  };
}

function slotForPick(pickNo: number, teams: number, type: string): number {
  if (teams <= 0) return 1;
  const round = Math.ceil(pickNo / teams);
  const indexInRound = ((pickNo - 1) % teams) + 1;
  if (type === 'linear') return indexInRound;
  return round % 2 === 0 ? teams - indexInRound + 1 : indexInRound;
}

function allPicksForSlot(slot: number, teams: number, rounds: number, type: string): number[] {
  const out: number[] = [];
  for (let round = 1; round <= rounds; round++) {
    const positionInRound = type === 'linear' ? slot : round % 2 === 0 ? teams - slot + 1 : slot;
    out.push((round - 1) * teams + positionInRound);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Sleeper's own ranking (§4.3.1) — the guaranteed floor of the ladder
// ---------------------------------------------------------------------------

/**
 * Build a ranking source from `search_rank` in the already-cached player map.
 *
 * §4.3.1 asks for this first so the tool always has at least one working
 * ranking even when every network adapter fails. Two things had to be handled
 * that the spec anticipated but could not know:
 *
 *  - `search_rank` is **not a clean 1..N overall ordering**. Ties are common
 *    (Josh Allen and Bijan Robinson both carry rank 1 in the 2026 payload), so
 *    the raw values cannot be used as ranks directly. They are re-ranked into a
 *    dense ordering here, with a deterministic tiebreak, so the board never
 *    reorders between polls (FR-39).
 *  - 9,999,999 is the "unranked" sentinel and is dropped upstream in
 *    `normalizePlayerUniverse`, not treated as a very deep rank.
 *
 * It is labelled "popularity" in the UI rather than "consensus", because that
 * is what it measures.
 */
export function sleeperRankingSource(universe: PlayerUniverse): import('../core/types.ts').RankingSource {
  const ranked = [...universe.values()]
    .filter((p) => p.search_rank !== null && p.position !== null)
    // A player with no NFL team cannot score fantasy points, so he does not
    // belong on a draft board — and `search_rank` will not tell you that.
    //
    // This is not a marginal filter. In the live 2026 payload, 981 of the 1,706
    // ranked fantasy players have no team, and their `search_rank` is frozen at
    // whatever it was when they last mattered: Todd Gurley sits at 27, Tom
    // Brady at 74, Antonio Brown at 89, Gronkowski at 118, Larry Fitzgerald at
    // 165. Without this filter the engine recommended a retired running back
    // second overall on a real draft board.
    //
    // Players are filtered here rather than dropped from the universe, so a
    // ranking list that names one still matches him and manual entry still
    // works. If a genuine free agent signs, Sleeper sets `team` and he
    // reappears on the next daily cache refresh.
    .filter((p) => p.team !== null)
    .sort((a, b) => {
      const byRank = (a.search_rank ?? Infinity) - (b.search_rank ?? Infinity);
      if (byRank !== 0) return byRank;
      // Deterministic tiebreak so the ordering is stable across reloads.
      return a.player_id_sleeper.localeCompare(b.player_id_sleeper);
    });

  const positionCounters = new Map<Position, number>();

  return {
    source_id: 'sleeper-search-rank',
    source_name: 'Sleeper popularity',
    source_url: 'https://docs.sleeper.com/',
    as_of_date: new Date().toISOString().slice(0, 10),
    scoring_format: 'unknown',
    tier: 1,
    kind: 'rank',
    unmatched: [],
    rows: ranked.map((player, i) => {
      const pos = player.position!;
      const positionRank = (positionCounters.get(pos) ?? 0) + 1;
      positionCounters.set(pos, positionRank);
      return {
        source_id: 'sleeper-search-rank',
        player_key: player.player_id_sleeper,
        raw_name: player.full_name,
        overall_rank: i + 1,
        position_rank: positionRank,
        tier: null,
        team: player.team,
        position: player.position,
        notes: null,
      };
    }),
  };
}
